'use strict';

/**
 * Import type definitions allowing VS Code to show IntelliSense.
 *
 * @typedef {import('./components/CommonMethods').default} commonMethods
 * @typedef {import('./components/NavigationHandler').default} NavigationHandler
 */

import Jets from 'jets';
import ClassLogger from 'ClassLogger';
import Countdown from './components/Countdown';
import { WidgetInstance } from 'friendly-challenge';
import SparkMD5 from 'spark-md5';

class Pausenhofkonzerte {
    /**
     * Returns the class name used by the ClassLogger.
     *
     * @returns {string}
     */
    getClassName () {
        return 'Pausenhofkonzerte';
    }

    constructor () {
        /** @type {console} */
        this.logger = ClassLogger(this, true); // set second parameter to false to disable logging

        /** @type {boolean} Set to true to disable captcha globally */
        this.disableCaptcha = true;

        if (window.navigationHandler) {
            this.addNavigationHandlerEvents();
        } else {
            window.addEventListener('navigationhandler:init', () => this.addNavigationHandlerEvents());
        }
    }

    addNavigationHandlerEvents () {
        window.navigationHandler.on('ready', () => this.init());
        window.navigationHandler.on('render', () => this.init());
    }

    /**
     * Initialize the song votings.
     *
     * @returns {this}
     */
    init () {
        this.logger.log(`Initializing the ${this.getClassName()} votings`);
        window.antenne.phk = {
            initCaptcha: (captchaElement, siteKey) => this.initCaptcha(captchaElement, siteKey),
            enableVoting: () => this.enableVoting(),
            initVotingButton: () => this.initVotingButton(),
            disableCaptcha: (disabled) => { this.disableCaptcha = disabled; },
        };

        this.initVotingButton();

        if (this.jetsInstance) {
            // Destroy previous instance
            this.jetsInstance.destroy();
            this.jetsInstance = undefined;
        }

        const schoolList = document.querySelectorAll('[data-pausenhofkonzerte-schoollist]');
        if (schoolList.length === 1) {
            this.jetsInstance = new Jets({
                searchTag: '[data-pausenhofkonzerte-schoolsearchinput]',
                contentTag: '[data-pausenhofkonzerte-schoollist]',
                addImportant: true,
            });
        }

        const countdowns = document.querySelectorAll('[data-countdown]');
        this.logger.log('countdowns found', { countdowns });
        countdowns.forEach((element) => {
            this.logger.log('Initializing coundown', element);
            let date = null;
            if (isNaN(element.dataset.countdown)) {
                date = Date.parse(element.dataset.countdown);
            } else {
                date = Date.now() + (element.dataset.countdown * 1000);
            }
            const deadline = new Date(date);
            const c = new Countdown(deadline, function () {
                if (element.dataset.onfinished && typeof window[element.dataset.onfinished] === 'function') {
                    window[element.dataset.onfinished]();
                }
            });
            element.appendChild(c.el);
        });
    }

    initCaptcha (captchaElement, siteKey) {
        if (this.disableCaptcha || !captchaElement || !siteKey) {
            this.logger.log('Captcha disabled or no element/siteKey provided, enabling voting button directly');
            this.enableVoting();
            return;
        }

        const self = this;
        const onMouseMove = (e) => {
            this.cursorHash.push(e.offsetX + 'x' + e.offsetY);
            // this.logger.log('tc cursor: ' + e.offsetX + 'x' + e.offsetY);
        };

        const container = captchaElement.closest('.l-container');

        // Note: cursorHash is already initialized in initVotingButton
        captchaElement.addEventListener('click', onMouseMove);

        // This element should contain the `frc-captcha` class for correct styling
        const captchaOptions = {
            startedCallback: function () {
                document.querySelector('input[name="nua"]').value = navigator.userAgent || 'na';
                self.logger.log('starting tc');
                container.addEventListener('mousemove', onMouseMove);
            },
            doneCallback: function (solution) {
                self.enableVoting();

                // const fcBanner = document.querySelector('.frc-banner');
                // if (fcBanner) {
                //     fcBanner.classList.add('u-hide');
                // }
            },
            errorCallback: function (error) {
                self.logger.error('Captcha error', error);
            },
            sitekey: siteKey,
            language: {
                text_init: 'Initialisierung..',

                text_ready: 'Anti-Roboter-Verifizierung',
                button_start: 'Hier klicken zum Start',

                text_fetching: 'Herausforderung laden..',

                text_solving: 'Verifizierung, dass du ein Mensch bist…',
                text_completed: 'Ich bin ein Mensch',
                text_completed_sr: 'Automatische Spamprüfung abgeschlossen',

                text_expired: 'Verifizierung abgelaufen',
                button_restart: 'Erneut starten',

                text_error: 'Verifizierung fehlgeschlagen',
                button_retry: 'Erneut versuchen',
                text_fetch_error: 'Verbindungsproblem mit',
            },
            solutionFieldName: 'frc-solution',
            puzzleEndpoint: 'https://eu-api.friendlycaptcha.eu/api/v1/puzzle',
            startMode: 'none',
        };

        self.logger.log('FriendlyCaptcha', captchaElement, captchaOptions);
        new WidgetInstance(captchaElement, captchaOptions);
        this.waitForCaptchaButton(captchaElement).then((button) => {
            button.classList.add('c-button');
        });
        // this makes the widget fetch a puzzle and start solving it.
        // captchaWidget.start();
    }

    initVotingButton () {
        const votingButton = document.querySelector('#votingButton');
        if (!votingButton) {
            return;
        }

        const self = this;
        const container = votingButton.closest('.l-container') || document.body;

        const onMouseMove = (e) => {
            this.cursorHash = this.cursorHash || [];
            this.cursorHash.push(e.offsetX + 'x' + e.offsetY);
        };

        this.cursorHash = [];
        container.addEventListener('mousemove', onMouseMove);

        votingButton.addEventListener('click', (e) => {
            // stop tc
            container.removeEventListener('mousemove', onMouseMove);
            const dataString = JSON.stringify(self.cursorHash);
            let hexHash = SparkMD5.hash(dataString);
            if (self.cursorHash.length === 0) {
                hexHash = 'none';
            }
            self.logger.log('tc result', { hash: hexHash, log: dataString });
            const chInput = document.querySelector('input[name="ch"]');
            if (chInput) {
                chInput.value = hexHash + '#' + dataString.substring(0, 1024);
            } else {
                self.logger.warn('ch input not found');
            }

            const nuaInput = document.querySelector('input[name="nua"]');
            if (nuaInput && !nuaInput.value) {
                nuaInput.value = navigator.userAgent || 'na';
            }
        });
    }

    enableVoting () {
        const votingButton = document.querySelector('#votingButton');
        if (votingButton) {
            votingButton.classList.remove('disabled');
            votingButton.classList.remove('u-hide');
            votingButton.innerHTML = 'Stimme abgeben';
        }
    }

    waitForCaptchaButton (element) {
        return new Promise((resolve) => {
            const check = () => {
                const button = element.querySelector('.frc-button');
                if (button) {
                    resolve(button);
                } else {
                    requestAnimationFrame(check);
                }
            };
            check();
        });
    }
}

new Pausenhofkonzerte();
